import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Countdown from "react-countdown";
import {
  FaTv,
  FaGift,
  FaLaptop,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const BASE_URL = "https://api.dreamtoday.in";

const Hero = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bannerIndex, setBannerIndex] = useState(0);
  const [imageIndex, setImageIndex] = useState(0);

  const navigate = useNavigate();

  /* FETCH API */
  useEffect(() => {
    fetch(`${BASE_URL}/api/admin/allbanner`)
      .then((r) => r.json())
      .then((d) => {
        if (d.success) setBanners(d.banners || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const banner = banners[bannerIndex];

  /* AUTO SLIDE BANNERS */
  useEffect(() => {
    if (!banners.length) return;

    const timer = setInterval(() => {
      setBannerIndex((p) => (p + 1) % banners.length);
      setImageIndex(0);
    }, 7000);

    return () => clearInterval(timer);
  }, [banners]);

  /* AUTO IMAGE SLIDE */
  useEffect(() => {
    if (banner?.type !== "images") return;

    const timer = setInterval(() => {
      setImageIndex((p) => (p + 1) % banner.media.length);
    }, 4000);

    return () => clearInterval(timer);
  }, [banner]);

  const title = banner?.title || "Win Luxury Prizes";
  const subtitle =
    banner?.subtitle ||
    "Join our premium campaigns and win big!";

  const floatingIcons = [
    { Icon: FaTv, delay: 0, className: "top-20 left-[6%]" },
    { Icon: FaLaptop, delay: 0.8, className: "bottom-24 right-[8%]" },
    { Icon: FaGift, delay: 1.5, className: "top-40 right-[18%]" },
  ];

  

  return (
    <section className="relative w-full min-h-[90vh] md:min-h-screen flex items-center justify-center overflow-hidden bg-black text-white">

      {/* BACKGROUND */}
      {!loading && banner ? (
        banner.type === "images" ? (
          <AnimatePresence mode="wait">
            <motion.img
              key={imageIndex}
              src={`${BASE_URL}${banner.media[imageIndex]}`}
              alt="banner"
              initial={{ opacity: 0, scale: 1.03 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="absolute inset-0 w-full h-full object-cover"
            />
          </AnimatePresence>
        ) : (
          <video
            key={banner.media[0]}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src={`${BASE_URL}${banner.media[0]}`} type="video/mp4" />
          </video>
        )
      ) : null}

      {/* OVERLAY */}
      <div className="absolute inset-0 bg-black/70" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(250,204,21,0.18),transparent_70%)]" />

      {/* FLOATING ICONS */}
      {floatingIcons.map(({ Icon, delay, className }, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 0.15, y: 0 }}
          transition={{
            delay,
            duration: 3,
            repeat: Infinity,
            repeatType: "reverse",
          }}
          className={`absolute ${className} text-yellow-500 text-5xl lg:text-6xl hidden lg:block`}
        >
          <Icon />
        </motion.div>
      ))}

      {/* CONTENT */}
      <div className="relative z-10 text-center px-6 max-w-6xl">

        <motion.h1
          key={title}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="font-extrabold leading-tight
          text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl mb-6"
        >
          {title}
        </motion.h1>

        <motion.p
          key={subtitle}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-gray-300
          text-sm sm:text-base md:text-lg lg:text-xl
          max-w-2xl mx-auto mb-10"
        >
          {subtitle}
        </motion.p>

        {/* BUTTON */}
        <div className="flex justify-center mb-12">
          <button
            onClick={() => navigate("/campaigns")}
            className="px-7 py-3 md:px-10 md:py-4
            rounded-full font-bold text-sm md:text-lg
            bg-gradient-to-r from-yellow-400 to-yellow-600 text-black
            hover:scale-105 active:scale-95 transition"
          >
            Participate Now
          </button>
        </div>
      </div>

      {/* ARROWS */}
      {banners.length > 1 && (
        <>
          <button
            onClick={() =>
              setBannerIndex(
                (p) => (p - 1 + banners.length) % banners.length
              )
            }
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2
            bg-black/40 backdrop-blur-lg
            border border-yellow-500/30
            p-2 md:p-3 rounded-full z-20"
          >
            <FaChevronLeft className="text-yellow-400 text-sm md:text-lg" />
          </button>

          <button
            onClick={() =>
              setBannerIndex((p) => (p + 1) % banners.length)
            }
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2
            bg-black/40 backdrop-blur-lg
            border border-yellow-500/30
            p-2 md:p-3 rounded-full z-20"
          >
            <FaChevronRight className="text-yellow-400 text-sm md:text-lg" />
          </button>
        </>
      )}

      {/* DOTS */}
      {banners.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-20">
          {banners.map((_, i) => (
            <button
              key={i}
              onClick={() => setBannerIndex(i)}
              className={`transition-all duration-300 rounded-full ${
                i === bannerIndex
                  ? "w-6 h-2 bg-yellow-400"
                  : "w-2 h-2 bg-yellow-400/40"
              }`}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default Hero;